<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ occupation ? occupation.title_en : $t('bo.esco.occupationDetail.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div v-if="loadingOcc" class="modal-body loading-state">{{ $t('bo.common.loading') }}</div>

      <template v-else-if="occupation">
        <div class="modal-body">
          <div v-if="error" class="error-banner">{{ error }}</div>
          <div v-if="success" class="success-banner">{{ success }}</div>

          <!-- Basic Info -->
          <section class="section">
            <h3 class="section-title">{{ $t('bo.esco.occupationDetail.basicInfo') }}</h3>
            <div class="info-grid">
              <div class="info-item">
                <label>{{ $t('bo.esco.skillDetail.escoUri') }}</label>
                <span class="mono">{{ occupation.esco_uri || '—' }}</span>
              </div>
              <div class="info-item">
                <label>{{ $t('bo.esco.colIscoCode') }}</label>
                <span>{{ occupation.isco_code || '—' }}</span>
              </div>
              <div v-if="occupation.manually_edited" class="info-item">
                <label>{{ $t('bo.esco.skillDetail.overrideFlags') }}</label>
                <div class="override-flags">
                  <span
                    v-for="(val, key) in (occupation.manual_overrides || {})"
                    :key="key"
                    class="flag"
                  >{{ key }}</span>
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('bo.esco.skillDetail.nameEn') }}</label>
                <input v-model="editForm.title_en" type="text" class="input" />
              </div>
              <div class="form-group">
                <label>{{ $t('bo.esco.skillDetail.nameFr') }}</label>
                <input v-model="editForm.title_fr" type="text" class="input" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('bo.esco.skillDetail.descEn') }}</label>
                <textarea v-model="editForm.description_en" class="textarea" rows="3" />
              </div>
              <div class="form-group">
                <label>{{ $t('bo.esco.skillDetail.descFr') }}</label>
                <textarea v-model="editForm.description_fr" class="textarea" rows="3" />
              </div>
            </div>
            <div class="save-row">
              <button @click="saveBasicInfo" :disabled="savingBasic" class="btn-save">
                {{ savingBasic ? $t('bo.common.saving') : $t('bo.esco.occupationDetail.saveBasicInfo') }}
              </button>
            </div>
          </section>

          <!-- Skills -->
          <section class="section">
            <div class="section-header">
              <h3 class="section-title">{{ $t('bo.esco.occupationDetail.skills', { count: skills.length }) }}</h3>
            </div>
            <table v-if="skills.length" class="sub-table">
              <thead>
                <tr>
                  <th>{{ $t('bo.esco.occupationDetail.colSkill') }}</th>
                  <th>{{ $t('bo.esco.colType') }}</th>
                  <th>{{ $t('bo.esco.occupationDetail.colRelation') }}</th>
                  <th>{{ $t('bo.esco.colImportance') }}</th>
                  <th>{{ $t('bo.esco.colLevel') }}</th>
                  <th>{{ $t('bo.esco.occupationDetail.colSource') }}</th>
                  <th>{{ $t('bo.common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="skill in skills" :key="skill.id">
                  <td>{{ skill.title }}</td>
                  <td>{{ skill.esco_skill_type }}</td>
                  <td>
                    <select
                      v-if="skill.manual_override"
                      :value="skill.relation_type"
                      @change="updateSkillRelation(skill, $event.target.value)"
                      class="rel-select"
                    >
                      <option value="essential">essential</option>
                      <option value="optional">optional</option>
                    </select>
                    <span v-else>{{ skill.relation_type }}</span>
                  </td>
                  <td>
                    <template v-if="skill.importance != null">
                      <div class="importance-bar-wrap" :title="`${Math.round(skill.importance * 100)}%`">
                        <div class="importance-bar" :style="{ width: (skill.importance * 100) + '%', background: importanceColor(skill.importance) }"></div>
                      </div>
                      <span class="importance-val">{{ Math.round(skill.importance * 100) }}%</span>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="skill.level != null" class="level-badge">{{ skill.level }}<span class="level-max">/7</span></span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span :class="['src-badge', `src-${skill.source}`]">{{ skill.source }}</span>
                    <span v-if="skill.manual_override" class="override-badge">override</span>
                  </td>
                  <td>
                    <button @click="removeSkill(skill)" class="btn-danger-sm">{{ $t('bo.common.remove') }}</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="sub-empty">{{ $t('bo.esco.occupationDetail.noSkills') }}</div>

            <!-- Add skill -->
            <div class="add-row">
              <input
                v-model="newSkillSearch"
                @input="debouncedSearchSkills"
                type="text"
                class="input"
                :placeholder="$t('bo.esco.occupationDetail.searchSkillPlaceholder')"
              />
              <select v-model="newSkillRelation" class="rel-select">
                <option value="essential">essential</option>
                <option value="optional">optional</option>
              </select>
              <button @click="addSkill" :disabled="!selectedSkillId || addingSkill" class="btn-add">
                {{ addingSkill ? $t('bo.common.adding') : $t('bo.esco.occupationDetail.addSkill') }}
              </button>
            </div>
            <div v-if="skillSuggestions.length" class="suggestions">
              <div
                v-for="s in skillSuggestions"
                :key="s.id"
                @click="selectSkill(s)"
                class="suggestion-item"
              >{{ s.title || s.name }}</div>
            </div>
          </section>

          <!-- Sectors -->
          <section class="section">
            <div class="section-header">
              <h3 class="section-title">{{ $t('bo.esco.occupationDetail.sectors', { count: sectors.length }) }}</h3>
            </div>
            <table v-if="sectors.length" class="sub-table">
              <thead>
                <tr>
                  <th>{{ $t('bo.esco.occupationDetail.colSector') }}</th>
                  <th>{{ $t('bo.esco.occupationDetail.colSource') }}</th>
                  <th>{{ $t('bo.esco.occupationDetail.colConfidence') }}</th>
                  <th>{{ $t('bo.common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sector in sectors" :key="sector.id">
                  <td>{{ sector.name }}</td>
                  <td><span :class="['src-badge', `src-${sector.source}`]">{{ sector.source }}</span></td>
                  <td>{{ sector.confidence !== null ? (sector.confidence * 100).toFixed(0) + '%' : '—' }}</td>
                  <td>
                    <button @click="removeSector(sector)" class="btn-danger-sm">{{ $t('bo.common.remove') }}</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="sub-empty">{{ $t('bo.esco.occupationDetail.noSectors') }}</div>

            <!-- Add sector -->
            <div class="add-row">
              <select v-model="newSectorId" class="input">
                <option value="">{{ $t('bo.esco.occupationDetail.selectSector') }}</option>
                <option
                  v-for="s in allSectors"
                  :key="s.id"
                  :value="s.id"
                  :disabled="sectors.some(existing => existing.id === s.id)"
                >
                  {{ s.sector_group?.name ? `${s.sector_group.name} / ` : '' }}{{ s.name }}
                </option>
              </select>
              <button @click="addSector" :disabled="!newSectorId || addingSector" class="btn-add">
                {{ addingSector ? $t('bo.common.adding') : $t('bo.esco.occupationDetail.addSector') }}
              </button>
            </div>
          </section>
        </div>

        <div class="modal-footer">
          <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.close') }}</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()

const props = defineProps({ occupationId: { type: Number, required: true } })
const emit = defineEmits(['close', 'updated'])

const occupation = ref(null)
const skills = ref([])
const sectors = ref([])
const loadingOcc = ref(true)
const error = ref('')
const success = ref('')
const savingBasic = ref(false)

const editForm = ref({ title_en: '', title_fr: '', description_en: '', description_fr: '' })

// Skill management
const newSkillSearch = ref('')
const skillSuggestions = ref([])
const selectedSkillId = ref(null)
const newSkillRelation = ref('essential')
const addingSkill = ref(false)
let debounceTimer = null

// Sector management
const allSectors = ref([])
const newSectorId = ref('')
const addingSector = ref(false)

const loadSectors = async () => {
  try {
    const res = await escoService.getSectors()
    allSectors.value = res.data || []
  } catch { /* non-blocking */ }
}

const loadOccupation = async () => {
  loadingOcc.value = true
  try {
    const res = await escoService.getOccupation(props.occupationId)
    occupation.value = res.data
    skills.value = res.data.skills || []
    sectors.value = res.data.sectors || []
    editForm.value = {
      title_en: res.data.title_en || '',
      title_fr: res.data.title_fr || '',
      description_en: res.data.description_en || '',
      description_fr: res.data.description_fr || '',
    }
  } catch {
    error.value = t('bo.esco.occupationDetail.failedLoad')
  } finally {
    loadingOcc.value = false
  }
}

const saveBasicInfo = async () => {
  savingBasic.value = true
  error.value = ''
  success.value = ''
  try {
    await escoService.updateOccupation(props.occupationId, editForm.value)
    success.value = t('bo.esco.occupationDetail.saveSuccess')
    await loadOccupation()
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.common.failedSave')
  } finally {
    savingBasic.value = false
  }
}

const debouncedSearchSkills = () => {
  clearTimeout(debounceTimer)
  selectedSkillId.value = null
  debounceTimer = setTimeout(searchSkills, 350)
}

const searchSkills = async () => {
  const q = newSkillSearch.value.trim()
  if (!q || q.length < 2) { skillSuggestions.value = []; return }
  try {
    const res = await escoService.getSkills({ search: q, per_page: 10 })
    skillSuggestions.value = res.data?.data || []
  } catch { skillSuggestions.value = [] }
}

const selectSkill = (s) => {
  selectedSkillId.value = s.id
  newSkillSearch.value = s.title || s.name
  skillSuggestions.value = []
}

const addSkill = async () => {
  if (!selectedSkillId.value) return
  addingSkill.value = true
  error.value = ''
  try {
    await escoService.addOccupationSkill(props.occupationId, {
      skill_id: selectedSkillId.value,
      relation_type: newSkillRelation.value,
    })
    newSkillSearch.value = ''
    selectedSkillId.value = null
    await loadOccupation()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.occupationDetail.failedAddSkill')
  } finally {
    addingSkill.value = false
  }
}

const updateSkillRelation = async (skill, relationType) => {
  try {
    await escoService.updateOccupationSkill(props.occupationId, skill.id, { relation_type: relationType })
    await loadOccupation()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.occupationDetail.failedUpdateRelation')
  }
}

const removeSkill = async (skill) => {
  if (!confirm(t('bo.esco.occupationDetail.confirmRemoveSkill', { title: skill.title }))) return
  try {
    await escoService.removeOccupationSkill(props.occupationId, skill.id)
    await loadOccupation()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.occupationDetail.failedRemoveSkill')
  }
}

const addSector = async () => {
  if (!newSectorId.value) return
  addingSector.value = true
  error.value = ''
  try {
    await escoService.addOccupationSector(props.occupationId, { sector_id: Number(newSectorId.value) })
    newSectorId.value = ''
    await loadOccupation()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.occupationDetail.failedAddSector')
  } finally {
    addingSector.value = false
  }
}

const removeSector = async (sector) => {
  if (!confirm(t('bo.esco.occupationDetail.confirmRemoveSector', { name: sector.name }))) return
  try {
    await escoService.removeOccupationSector(props.occupationId, sector.id)
    await loadOccupation()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.occupationDetail.failedRemoveSector')
  }
}

const importanceColor = (val) => {
  if (val >= 0.7) return '#10b981'
  if (val >= 0.4) return '#f59e0b'
  return '#6b7280'
}

onMounted(() => { loadOccupation(); loadSectors() })
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.loading-state {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-banner {
  background: #fef2f2; border: 1px solid #fecaca; color: #dc2626;
  padding: 0.75rem 1rem; border-radius: 0.375rem; font-size: 0.875rem;
}

.success-banner {
  background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534;
  padding: 0.75rem 1rem; border-radius: 0.375rem; font-size: 0.875rem;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.info-item label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.mono { font-family: monospace; font-size: 0.85rem; color: #374151; }

.override-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.flag {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
}

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
  background: white;
}

.input:focus { outline: none; border-color: #3b82f6; }

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
  background: white;
}

.textarea:focus { outline: none; border-color: #3b82f6; }

.save-row {
  display: flex;
  justify-content: flex-end;
}

.btn-save {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-save:hover:not(:disabled) { background: #2563eb; }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

.sub-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  background: white;
  border-radius: 0.375rem;
  overflow: hidden;
}

.sub-table th {
  padding: 0.5rem 0.75rem;
  background: #e5e7eb;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
}

.sub-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.sub-table tr:last-child td { border-bottom: none; }

.sub-empty { color: #6b7280; font-size: 0.875rem; padding: 0.5rem 0; }

.src-badge {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.src-esco { background: #dbeafe; color: #1e40af; }
.src-manual { background: #fef3c7; color: #92400e; }
.src-inferred { background: #f3f4f6; color: #374151; }

.override-badge {
  display: inline-block;
  margin-left: 0.3rem;
  padding: 0.1rem 0.4rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 9999px;
  font-size: 0.7rem;
}

.rel-select {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background: white;
}

.add-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.btn-add {
  padding: 0.45rem 0.9rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.btn-add:hover:not(:disabled) { background: #059669; }
.btn-add:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-danger-sm {
  padding: 0.25rem 0.6rem;
  border: 1px solid #ef4444;
  color: #ef4444;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.75rem;
}

.btn-danger-sm:hover { background: #fef2f2; }

.suggestions {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;
  max-height: 180px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  border-bottom: 1px solid #f3f4f6;
}

.suggestion-item:last-child { border-bottom: none; }
.suggestion-item:hover { background: #eff6ff; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.importance-bar-wrap {
  width: 60px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
}
.importance-bar {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.3s;
}
.importance-val {
  font-size: 0.72rem;
  color: #6b7280;
  margin-left: 0.3rem;
  vertical-align: middle;
}
.level-badge {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}
.level-max { color: #93c5fd; font-weight: 400; }
.text-muted { color: #9ca3af; font-size: 0.8rem; }
</style>
